import { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Typography,
  Divider,
  Modal,
} from "@mui/material";
import ModalBox from "../../themes/overrides/ModalBox";
import { Add } from "@mui/icons-material";
import DeleteIcon from "../../assets/images/icons/delete_icon.svg";
import AceEditor from "react-ace";
import { addWebHook, updateWebHook } from "../../actions/webHookAction";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks/use-dispatch";
import { toast } from "sonner";
import { fields, operators, requestTypes } from "../../constant/webhooks";
import { useTriggers } from "../../hooks/webhooks/use-triggers";
import { webhookSchema } from "../../validations/web-hook";
import { transferZodErrors } from "../../utils/transfer-zod-errors";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";

const CreateWebhooks = ({ open, onClose, editMode, webhook, onSuccess }) => {
  const [name, setName] = useState("");
  const [requestType, setRequestType] = useState("GET");
  const [apiEndpoint, setApiEndpoint] = useState("");
  const [errors, setErrors] = useState({});
  const [active, setActive] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [filters, setFilters] = useState([]);
  const [payload, setPayload] = useState({});
  const { workspaceId } = useParams();
  const {
    triggers,
    setTriggers,
    toggleTriggerAction,
    isActionChecked,
    resetTriggers,
  } = useTriggers();
  const dispatch = useAppDispatch();

  // Handle changes in filters and headers
  const handleFilterChange = (index, field, value) => {
    const updatedFilters = [...filters];
    updatedFilters[index][field] = value;
    setFilters(updatedFilters);
  };

  // Add new filter
  const addFilter = () => {
    setFilters([
      ...filters,
      { value: "", operator: "eq", field: "workspaceId" },
    ]);
  };

  // Remove existing filter
  const removeFilter = (index) => {
    const updatedFilters = [...filters];
    // Remove the filter at the specified index
    updatedFilters.splice(index, 1);
    // Update the state with the new filters array
    setFilters(updatedFilters ?? []);
  };

  // Handle changes in headers
  const handleHeaderChange = (index, field, value) => {
    const updatedHeaders = [...headers];
    updatedHeaders[index][field] = value;
    setHeaders(updatedHeaders);
  };

  // Add new header
  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  // Remove existing header
  const removeHeader = (index) => {
    const updatedHeaders = [...headers];
    // Remove the header at the specified index
    updatedHeaders.splice(index, 1);
    // Update the state with the new headers array
    setHeaders(updatedHeaders);
  };

  // Handle checkbox changes for triggers
  const handleCheckboxChange = toggleTriggerAction;

  // Check if a specific action is checked for a content type
  const isChecked = isActionChecked;

  // validate single field
  const validateField = (field, value) => {
    const result = webhookSchema?.shape?.[field].safeParse(value);
    if (!result.success) {
      setErrors((prev) => ({
        ...prev,
        [field]: transferZodErrors(result.error)?.["custom"] ?? [],
      }));
    } else {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const data = {
      name,
      requestType,
      apiEndpoint,
      filters,
      headers,
      payload,
      active,
      triggers,
      workspaceId,
    };

    // validate data
    const validatedData = webhookSchema.safeParse(data);

    if (!validatedData.success) {
      setErrors(transferZodErrors(validatedData.error));
      return;
    }

    const alert = toast.loading("Event is being processed...");
    try {
      let state;
      if (editMode) {
        state = await dispatch(updateWebHook(webhook._id, data));
      } else {
        state = await dispatch(addWebHook(data));
      }

      // handle response state
      if (state?.success) {
        onSuccess?.();
        toast.success(
          state?.message ||
            `Webhook ${editMode ? "updated" : "created"} successfully`,
          {
            id: alert,
          }
        );
        resetFields();
        onClose();
      } else if (state.status === 400) {
        setErrors(state?.message || {});
        toast.error("Please fix the errors in the form", {
          id: alert,
        });
      } else {
        toast.error(JSON.stringify(state?.message || "Something went wrong"), {
          id: alert,
        });
      }
    } catch (error) {
      toast.error(JSON.stringify(error?.message || "Something went wrong"), {
        id: alert,
      });
    }
  };

  // Reset form fields
  const resetFields = useCallback(() => {
    setName("");
    setRequestType("GET");
    setApiEndpoint("");
    setActive(false);
    setHeaders([]);
    setFilters([]);
    setPayload({});
    resetTriggers();

    // reset errors as well
    setErrors({});
  }, [resetTriggers]);

  // Set initial values when in create/edit modes
  useEffect(() => {
    if (editMode && webhook) {
      setName(webhook.name);
      setRequestType(webhook.requestType);
      setApiEndpoint(webhook.apiEndpoint);
      setActive(webhook.active);
      setHeaders(webhook.headers);
      setFilters(webhook.filters);
      setPayload(webhook.payload);
      setTriggers(webhook.triggers || []);
    } else {
      resetFields();
    }
  }, [editMode, resetFields, resetTriggers, setTriggers, webhook]);

  return (
    <>
      <Modal open={open} onClose={() => {
        resetFields();
        onClose();
      }}>
        <ModalBox sx={{ width: "800px", maxWidth: "95%" }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Create WebHooks
          </Typography>

          {/* Name Field */}
          <TextField
            label="Name"
            value={name}
            required
            onChange={(e) => {
              setName(e.target.value);
              validateField("name", e.target.value);
            }}
            fullWidth
            error={!!errors?.name}
            helperText={errors?.name ? errors?.name[0] : ""}
            margin="normal"
            variant="outlined"
            size="small"
            sx={{
              "& .MuiInputLabel-root": {
                fontSize: "1rem",
              },
              "& .MuiInputBase-input": {
                fontSize: "1.1rem",
              },
            }}
          />

          {/* API Configuration */}
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel sx={{ fontSize: "0.92rem" }}>Request Type</InputLabel>
              <Select
                value={requestType}
                onChange={(e) => setRequestType(e.target.value)}
                label="Request Type"
                sx={{
                  fontSize: "1rem",
                  "& .MuiSelect-select": {
                    py: 1.2,
                  },
                }}
              >
                {requestTypes.map((type) => (
                  <MenuItem
                    sx={{ fontSize: "0.875rem" }}
                    value={type}
                    key={type}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="API Endpoint"
              value={apiEndpoint}
              required
              onChange={(e) => {
                setApiEndpoint(e.target.value);
                validateField("apiEndpoint", e.target.value);
              }}
              error={!!errors?.apiEndpoint}
              helperText={errors?.apiEndpoint ? errors?.apiEndpoint[0] : ""}
              fullWidth
              variant="outlined"
              size="small"
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                },
                "& .MuiInputBase-input": {
                  fontSize: "1.1rem",
                },
              }}
            />
          </Box>

          {/* Active Toggle */}
          <label className="inline-flex items-center cursor-pointer mt-4">
            <input
              type="checkbox"
              value=""
              className="sr-only peer"
              checked={active}
              onChange={() => setActive(!active)}
            />
            <div className="relative w-11 h-6 rounded-full peer bg-gray-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
            <span className="ms-3 text-md font-medium">Active</span>
          </label>

          <Divider sx={{ my: 3 }} />

          {/* Triggers Table */}
          <div className="relative overflow-x-auto sm:rounded-lg text-black">
            <table className="w-full text-sm text-left rtl:text-right">
              <caption className="py-3 text-lg font-semibold text-left rtl:text-right bg-white">
                Triggers <span style={{ color: "red" }}>*</span>
                <p className="mt-1 text-sm font-normal text-gray-600">
                  Manage your webhook triggers, and customize which type of
                  event should the webhook trigger
                </p>
              </caption>
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  {["Name", "Publish", "Save", "Delete"].map((header) => (
                    <th key={header} scope="col" className="px-6 py-3">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {["Content Model", "Content", "Media"].map((contentType) => (
                  <tr
                    key={contentType}
                    className="bg-white border-b border-gray-200"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium whitespace-nowrap text-gray-900"
                    >
                      {contentType}
                    </th>
                    {["Create", "Save", "Delete"].map((action) => (
                      <td
                        key={`${contentType}-${action}`}
                        className="px-6 py-4"
                      >
                        <div className="flex items-center">
                          <input
                            id={`checkbox-${contentType}-${action}`}
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            checked={isChecked(contentType, action)}
                            onChange={() => {
                              const trigger = handleCheckboxChange(
                                contentType,
                                action
                              );
                              validateField("triggers", trigger);
                            }}
                          />
                          <label
                            htmlFor={`checkbox-${contentType}-${action}`}
                            className="sr-only"
                          >
                            checkbox
                          </label>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* error message */}
            <p className="text-red-500 text-sm mt-2">
              {errors?.triggers && errors?.triggers[0]
                ? errors.triggers[0]
                : ""}
            </p>
          </div>

          <Divider sx={{ my: 3 }} />

          {/* Filter */}
          <h2 className="py-3 text-lg font-semibold text-left rtl:text-right bg-white">
            Filters
          </h2>
          {filters.map((filter, index) => (
            <Box
              key={index}
              sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center" }}
            >
              {/* Field selector */}
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel sx={{ fontSize: "1rem" }}>Field</InputLabel>
                <Select
                  value={filter.field}
                  onChange={(e) =>
                    handleFilterChange(index, "field", e.target.value)
                  }
                  label="Field"
                  sx={{
                    fontSize: "1rem",
                    "& .MuiSelect-select": {
                      py: 1.2,
                    },
                  }}
                >
                  {fields.map((field) => (
                    <MenuItem
                      key={field.value}
                      value={field.value}
                      sx={{ fontSize: "1rem" }}
                    >
                      {field.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Operator selector */}
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel sx={{ fontSize: "1rem" }}>Operator</InputLabel>
                <Select
                  value={filter.operator || "eq"}
                  onChange={(e) =>
                    handleFilterChange(index, "operator", e.target.value)
                  }
                  label="Operator"
                  sx={{
                    fontSize: "1rem",
                    "& .MuiSelect-select": {
                      py: 1.2,
                    },
                  }}
                >
                  {operators.map((operator) => (
                    <MenuItem
                      key={operator.value}
                      value={operator.value}
                      sx={{ fontSize: "1rem" }}
                    >
                      {operator.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Value input */}
              <TextField
                label="Value"
                value={filter.value}
                onChange={(e) =>
                  handleFilterChange(index, "value", e.target.value)
                }
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "1rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                  },
                }}
              />
              <IconButton
                onClick={() => removeFilter(index)}
                sx={{
                  color: "#FF4842",
                }}
              >
                <img src={DeleteIcon} alt="delete" />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<Add />}
            onClick={addFilter}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Filter
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* Headers */}
          <h2 className="py-3 text-lg font-semibold text-left rtl:text-right bg-white">
            Headers
          </h2>
          {headers.map((header, index) => (
            <Box key={index} sx={{ display: "flex", gap: 2, mb: 1 }}>
              {/* Header Key */}
              <TextField
                label="Key"
                value={header.key}
                onChange={(e) =>
                  handleHeaderChange(index, "key", e.target.value)
                }
                size="small"
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "1rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                  },
                }}
              />

              {/* Header Value */}
              <TextField
                label="Value"
                value={header.value}
                onChange={(e) =>
                  handleHeaderChange(index, "value", e.target.value)
                }
                size="small"
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "1rem",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                  },
                }}
                fullWidth
              />
              <IconButton
                onClick={() => removeHeader(index)}
                sx={{
                  color: "#FF4842",
                }}
              >
                <img src={DeleteIcon} alt="delete" />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={addHeader}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Custom Header
          </Button>

          <Divider sx={{ my: 3 }} />

          {/* Payload */}
          <div className="mb-4">
            <h2 className="py-3 text-lg font-semibold text-left rtl:text-right bg-white">
              Payload
            </h2>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enter the JSON payload that will be sent with the webhook.
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <AceEditor
                mode="json"
                theme="github"
                name="payload-editor"
                width="100%"
                height="200px"
                value={JSON.stringify(payload, null, 2)}
                onChange={(value) => {
                  try {
                    const parsed = JSON.parse(value);
                    setPayload(parsed);
                  } catch (e) {}
                }}
                fontSize={14}
                showPrintMargin={false}
                showGutter={true}
                highlightActiveLine={true}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2,
                  useWorker: false,
                }}
                editorProps={{ $blockScrolling: true }}
                style={{
                  backgroundColor: "#f8f9fa",
                  fontFamily: '"Fira Code", "Courier New", monospace',
                }}
              />
              {/* error message */}
              <p className="text-red-500 text-sm mt-2">
                {errors?.payload && errors?.payload[0] ? errors.payload[0] : ""}
              </p>
            </Box>
          </div>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => {
              resetFields();
              onClose();
            }}>
              Close
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Box>
        </ModalBox>
      </Modal>
    </>
  );
};

export default CreateWebhooks;
